import { Navigate, Link } from 'react-router-dom';
import { bg, line, tx, font, accent, space, radius, attrColour } from '../theme';
import Stat from '../components/Stat';
import { useClub } from './ClubContext';
import ClubCrest from './ClubCrest';
import {
  fmtMoney, ageFromDob, groupFor, GROUP_ORDER,
} from './gameUtils';

const TIER_NAMES = {
  1: 'Premier League', 2: 'Championship', 3: 'League One', 4: 'League Two',
};

function Screen({ children }) {
  return (
    <div style={{
      minHeight: '60vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: tx.muted, fontFamily: font.display,
      fontSize: 12, letterSpacing: '0.2em', textTransform: 'uppercase',
    }}>
      {children}
    </div>
  );
}

export default function Home() {
  const { club, finances, squad, status, saveId } = useClub();

  if (status === 'notfound') return <Navigate to="/hub" replace />;
  if (status === 'loading') return <Screen>Loading your club</Screen>;
  if (status === 'error') return <Screen>Couldn&rsquo;t reach the world database</Screen>;
  if (status === 'empty' || !club) return <Screen>Club not found</Screen>;

  const list = squad || [];
  const size = list.length;
  const rated = list.filter((p) => Number(p.current_ability) > 0);
  const avgCA = rated.length
    ? Math.round(rated.reduce((s, p) => s + Number(p.current_ability), 0) / rated.length)
    : 0;
  const wageBill = list.reduce((s, p) => s + (Number(p.wage) || 0), 0);
  const squadValue = list.reduce((s, p) => s + (Number(p.market_value) || 0), 0);

  const transfer = finances && finances.transfer_budget != null
    ? fmtMoney(finances.transfer_budget) : '—';
  const wageBudget = finances && finances.wage_budget != null
    ? Number(finances.wage_budget) : null;
  const wagePct = wageBudget ? Math.round((wageBill / wageBudget) * 100) : null;

  const counts = GROUP_ORDER.reduce((acc, g) => {
    acc[g] = list.filter((p) => groupFor(p.primary_position) === g).length;
    return acc;
  }, {});

  const injured = list.filter((p) => /injur/i.test(p.status || '')).length;
  const top = [...rated]
    .sort((a, b) => Number(b.current_ability) - Number(a.current_ability))
    .slice(0, 6);

  return (
    <div style={{ padding: space.s5, maxWidth: 1400 }}>
      {/* Club header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: space.s4,
        marginBottom: space.s5, paddingBottom: space.s5,
        borderBottom: `1px solid ${line.DEFAULT}`,
      }}>
        <ClubCrest club={club} size={64} />
        <div style={{ minWidth: 0 }}>
          <div style={{
            fontFamily: font.display, fontSize: 11, fontWeight: 700,
            letterSpacing: '0.2em', textTransform: 'uppercase',
            color: accent.lime, marginBottom: space.s1,
          }}>
            {TIER_NAMES[club.tier] || `Tier ${club.tier}`}
          </div>
          <h1 style={{
            fontFamily: font.display, fontSize: 26, fontWeight: 700,
            letterSpacing: '0.01em', margin: 0,
          }}>
            {club.name}
          </h1>
          <div style={{
            fontFamily: font.data, fontSize: 12, color: tx.muted, marginTop: 4,
          }}>
            {club.town}
            {club.nickname ? ` · ${club.nickname}` : ''}
            {club.stadium ? ` · ${club.stadium}` : ''}
          </div>
        </div>
      </div>

      {/* Stat tiles */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: space.s3, marginBottom: space.s5,
      }}>
        <Stat
          label="Transfer Budget"
          value={transfer}
          sub={finances ? 'Set by the board' : 'Awaiting board review'}
          tone="lime"
        />
        <Stat
          label="Wage Bill"
          value={`${fmtMoney(wageBill)}/wk`}
          sub={wagePct != null ? `${wagePct}% of wage budget` : 'Weekly squad wages'}
          tone={wagePct != null && wagePct > 90 ? 'amber' : 'cyan'}
          subTone={wagePct != null && wagePct > 100 ? 'red' : undefined}
        />
        <Stat
          label="Squad"
          value={String(size)}
          sub={injured ? `${injured} injured` : `Avg ability ${avgCA}`}
          tone="violet"
          subTone={injured ? 'amber' : undefined}
        />
        <Stat
          label="Squad Value"
          value={fmtMoney(squadValue)}
          sub="Estimated market value"
          tone="green"
        />
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: space.s4,
        alignItems: 'start',
      }}>
        {/* Squad shape */}
        <div style={{
          background: bg.surface, border: `1px solid ${line.DEFAULT}`,
          borderRadius: radius.lg, overflow: 'hidden',
        }}>
          <div style={{
            padding: `${space.s3}px ${space.s4}px`,
            borderBottom: `1px solid ${line.DEFAULT}`,
            fontFamily: font.display, fontSize: 12, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: tx.muted,
          }}>
            Squad shape
          </div>
          <div style={{ padding: space.s4, display: 'grid', gap: space.s3 }}>
            {GROUP_ORDER.map((g) => {
              const n = counts[g];
              const pct = size ? Math.round((n / size) * 100) : 0;
              return (
                <div key={g}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between',
                    fontFamily: font.data, fontSize: 12, color: tx.secondary,
                    marginBottom: 6,
                  }}>
                    <span>{{ GK: 'Goalkeepers', DEF: 'Defenders', MID: 'Midfielders', FWD: 'Forwards' }[g]}</span>
                    <span style={{ color: tx.primary }}>{n}</span>
                  </div>
                  <div style={{
                    height: 6, borderRadius: 3, background: bg.overlay,
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${pct}%`, height: '100%', background: accent.cyan,
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Key players */}
        <div style={{
          background: bg.surface, border: `1px solid ${line.DEFAULT}`,
          borderRadius: radius.lg, overflow: 'hidden',
        }}>
          <div style={{
            padding: `${space.s3}px ${space.s4}px`,
            borderBottom: `1px solid ${line.DEFAULT}`,
            display: 'flex', alignItems: 'center',
            fontFamily: font.display, fontSize: 12, fontWeight: 600,
            letterSpacing: '0.12em', textTransform: 'uppercase', color: tx.muted,
          }}>
            Key players
            <Link to={`/game/${saveId}/squad`} style={{
              marginLeft: 'auto', color: accent.lime, textDecoration: 'none',
              fontSize: 11, letterSpacing: '0.1em',
            }}>
              Full squad &rsaquo;
            </Link>
          </div>
          <div>
            {top.map((p, i) => {
              const age = ageFromDob(p.dob);
              return (
                <div key={p.id} style={{
                  display: 'flex', alignItems: 'center', gap: space.s3,
                  padding: `${space.s3}px ${space.s4}px`,
                  borderTop: i === 0 ? 'none' : `1px solid ${line.DEFAULT}`,
                }}>
                  <span style={{
                    fontFamily: font.data, fontSize: 11, color: tx.faint,
                    width: 28,
                  }}>
                    {p.primary_position}
                  </span>
                  <span style={{
                    flex: 1, fontFamily: font.display, fontWeight: 600,
                    fontSize: 14, color: tx.primary, whiteSpace: 'nowrap',
                    overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {p.first_name} {p.last_name}
                  </span>
                  <span style={{ fontFamily: font.data, fontSize: 12, color: tx.muted }}>
                    {age != null ? `${age}y` : ''}
                  </span>
                  <span style={{
                    fontFamily: font.data, fontSize: 14, fontWeight: 700,
                    color: attrColour(Number(p.current_ability)), width: 34,
                    textAlign: 'right',
                  }}>
                    {p.current_ability || '—'}
                  </span>
                </div>
              );
            })}
            {top.length === 0 && (
              <div style={{ padding: space.s4, color: tx.faint, fontFamily: font.data, fontSize: 12 }}>
                No players on the books yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
